import { Check, FileText, Home, Building } from "lucide-react";

const additionalInfo = [
  {
    title: "Estimation offerte",
    description: "Nous réalisons une Étude Comparative de Marché (ECM) offerte et sans engagement pour estimer la valeur de votre bien."
  },
  {
    title: "Paiement au succès",
    description: "Vous ne payez que si votre bien est vendu. Aucuns frais en cas de non-vente."
  },
  {
    title: "Transparence totale",
    description: "Tous nos honoraires sont affichés clairement, sans frais cachés ni mauvaises surprises."
  },
  {
    title: "Accompagnement complet",
    description: "De l'estimation à la signature chez le notaire, nous gérons toute la partie administrative et juridique."
  }
];

export default function HonorairesPage() {
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="honoraires-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B5AD] mb-3">
            Nos honoraires
          </p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-light text-zinc-900">
            Honoraires <span className="font-medium">transparents</span>
          </h1>
          <p className="mt-4 text-zinc-600 max-w-2xl">
            Des honoraires clairs et compétitifs, sans frais cachés. 
            Nous sommes rémunérés uniquement si votre bien est vendu.
          </p>
        </div>
      </div>

      {/* Legal Notice */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-zinc-100 border-l-4 border-[#00B5AD] p-6" data-testid="legal-notice">
          <p className="text-sm text-zinc-600 leading-relaxed">
            Suite à l'Arrêté n° 83-50/A du 3 octobre 1983 relatif à la publicité des prix de tous les services, 
            Modifié par Arrêté du 15 juillet 2010, BOCC n° 8 du 9 septembre 2010, Art. 1, 
            il est porté à l'attention de la clientèle que :
          </p>
          <p className="mt-3 text-2xl font-['Outfit'] font-bold text-zinc-900">
            Montant maximum de nos honoraires = 8%
          </p>
        </div>
      </div>

      {/* Main Pricing Card */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-[#D62965] ring-1 ring-[#D62965] p-8" data-testid="pricing-card-main">
            <div className="bg-[#D62965] text-white text-xs uppercase tracking-wider px-3 py-1 inline-block mb-4">
              Honoraires de vente
            </div>
            <h3 className="font-['Outfit'] text-2xl font-medium text-zinc-900 mb-4">
              Commission de vente
            </h3>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-['Outfit'] text-5xl font-bold text-zinc-900">
                8%
              </span>
              <span className="text-zinc-500 text-lg">maximum TTC</span>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Estimation offerte et sans engagement</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Shooting photos professionnelles HDR ou 4K</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Visite virtuelle 360°</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Photos et vidéo par drone</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Diffusion premium sur 30+ portails immobiliers</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Marketing digital ciblé</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-[#00B5AD] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Accompagnement complet jusqu'à la signature</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Avis de valeur */}
      <div className="bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-['Outfit'] text-2xl font-medium text-zinc-900 mb-8 text-center">
            Avis de valeur
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Avis de valeur locative */}
            <div className="border border-zinc-200 p-6" data-testid="avis-valeur-locative">
              <div className="flex items-center gap-3 mb-4">
                <Building className="h-8 w-8 text-[#00B5AD]" />
                <h4 className="font-['Outfit'] font-medium text-zinc-900">Avis de valeur locative</h4>
              </div>
              <ul className="space-y-3 text-sm text-zinc-600">
                <li className="flex justify-between items-center py-2 border-b border-zinc-100">
                  <span>Habitation</span>
                  <span className="font-semibold text-zinc-900">300 € TTC</span>
                </li>
                <li className="flex justify-between items-center py-2">
                  <span>Local commercial ou professionnel</span>
                  <span className="font-semibold text-zinc-900">400 € HT</span>
                </li>
              </ul>
            </div>

            {/* Avis de valeur habitation */}
            <div className="border border-zinc-200 p-6" data-testid="avis-valeur-habitation">
              <div className="flex items-center gap-3 mb-4">
                <Home className="h-8 w-8 text-[#00B5AD]" />
                <h4 className="font-['Outfit'] font-medium text-zinc-900">Avis de valeur pour habitation</h4>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-['Outfit'] text-3xl font-bold text-zinc-900">150 €</span>
              </div>
              <div className="bg-[#00B5AD]/10 text-[#00B5AD] text-sm font-medium px-3 py-2 rounded">
                <FileText className="h-4 w-4 inline mr-2" />
                Offert dans le cadre de la signature d'un mandat
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-['Outfit'] text-2xl font-medium text-zinc-900 mb-8 text-center">
            Nos engagements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalInfo.map((info, idx) => (
              <div key={idx} className="bg-white border border-zinc-200 p-6" data-testid={`engagement-${idx}`}>
                <h4 className="font-['Outfit'] font-medium text-zinc-900 mb-2">
                  {info.title}
                </h4>
                <p className="text-sm text-zinc-600 leading-relaxed">
                  {info.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
